import React, { useMemo, useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, Polyline, useMap } from 'react-leaflet';
import { useSearchParams } from 'react-router-dom';
import L from 'leaflet';
import API_BASE_URL from '../../config';

const makeIcon = (color) => L.divIcon({
  className: '',
  html: `<div style="
    width:18px;height:18px;border-radius:50%;
    background:${color};border:2px solid #fff;
    box-shadow:0 1px 4px rgba(0,0,0,0.45);
  "></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  popupAnchor: [0, -10]
});

const servicedIcon = makeIcon('#2196F3');
const inspectionPendingIcon = makeIcon('#FBC02D');
const inspectionDoneIcon = makeIcon('#43A047');
const selectedRouteIcon = makeIcon('#FB8C00');
const myLocationIcon = L.divIcon({
  className: '',
  html: `<div style="
    width:16px;height:16px;border-radius:50%;
    background:#1E88E5;border:3px solid #fff;
    box-shadow:0 0 0 8px rgba(30,136,229,0.28);
  "></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  popupAnchor: [0, -10]
});

const MAP_LAYERS = {
  street: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors'
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri'
  },
  hybrid: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
    overlays: [
      {
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}',
        attribution: 'Roads &copy; Esri'
      },
      {
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
        attribution: 'Labels &copy; Esri'
      }
    ]
  }
};

const getKind = (device) => {
  const kind = device?.device_kind || 'serviced';
  if (kind === 'normal' || kind === 'owned') return 'serviced';
  return kind;
};

const kindLabel = (kind) => {
  if (kind === 'inspection') return 'Inspection';
  if (kind === 'me') return 'Me';
  return 'Serviced';
};

const hasInspectionReport = (device) => Number(device?.report_count || 0) > 0;

const inspectionStatusLabel = (device) => (
  hasInspectionReport(device) ? 'Inspection (reported)' : 'Inspection (pending)'
);

const deviceKey = (device) => `${getKind(device)}:${device.device_id}`;

function DeviceMarker({
  device,
  icon,
  isPinned,
  onPin,
  onUnpin,
  selectedIds,
  toggleSelected
}) {
  const markerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const pinnedRef = useRef(isPinned);
  const kind = getKind(device);
  const key = deviceKey(device);

  useEffect(() => {
    pinnedRef.current = isPinned;
  }, [isPinned]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openPopup = () => {
    clearCloseTimer();
    markerRef.current?.openPopup();
  };

  const scheduleClose = () => {
    clearCloseTimer();
    if (pinnedRef.current) return;
    closeTimerRef.current = setTimeout(() => {
      if (!pinnedRef.current) markerRef.current?.closePopup();
    }, 220);
  };

  useEffect(() => {
    if (isPinned) openPopup();
  }, [isPinned]);

  useEffect(() => () => clearCloseTimer(), []);

  return (
    <Marker
      ref={markerRef}
      position={[parseFloat(device.last_known_lat), parseFloat(device.last_known_lon)]}
      icon={icon}
      eventHandlers={{
        mouseover: openPopup,
        mouseout: scheduleClose,
        click: () => {
          onPin(key);
          openPopup();
        }
      }}
    >
      <Popup
        autoClose={false}
        closeOnClick={false}
        eventHandlers={{
          mouseover: clearCloseTimer,
          mouseout: scheduleClose,
          remove: () => {
            if (isPinned) onUnpin(key);
          }
        }}
      >
        <div
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleClose}
        >
          <strong>{device.device_id}</strong><br />
          <em>{device.description || 'No description'}</em><br />
          <div><strong>Type:</strong> {kind === 'inspection' ? inspectionStatusLabel(device) : kindLabel(kind)}</div>
          {kind === 'inspection' && (
            <div><strong>Reports:</strong> {Number(device.report_count || 0)}</div>
          )}
          <hr />
          <div>
            <strong>Location:</strong>{' '}
            {[device.street_address, device.suburb, device.state, device.country, device.postcode]
              .filter(Boolean)
              .join(', ') || '—'}
          </div>
          <div>
            <strong>Coordinates:</strong> {device.last_known_lat}, {device.last_known_lon}
          </div>
          {device.direction && <div><strong>Direction:</strong> {device.direction}</div>}
          <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => toggleSelected(key)}
              style={{
                width: 'auto',
                border: '1px solid #ccc',
                background: selectedIds.includes(key) ? '#e8f5e9' : '#fff',
                borderRadius: 4,
                padding: '4px 8px',
                cursor: 'pointer',
                color: '#333'
              }}
            >
              {selectedIds.includes(key) ? 'Unselect for route' : 'Select for route'}
            </button>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(device.last_known_lat)},${encodeURIComponent(device.last_known_lon)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Navigate here
            </a>
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

function FlyToMarker({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 15, { duration: 1.5 });
    }
  }, [position, map]);
  return null;
}

function FitRoute({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions && positions.length > 1) {
      map.fitBounds(positions, { padding: [40, 40] });
    }
  }, [positions, map]);
  return null;
}

const haversineKm = (a, b) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLon = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const nearestNeighborOrder = (points, startPoint = null) => {
  if (!points.length) return startPoint ? [startPoint] : [];
  if (!startPoint && points.length <= 2) return points;

  const remaining = [...points];
  const ordered = [];
  if (startPoint) {
    ordered.push(startPoint);
  } else {
    ordered.push(remaining.shift());
  }

  while (remaining.length) {
    const current = ordered[ordered.length - 1];
    let bestIndex = 0;
    let bestDistance = Infinity;
    remaining.forEach((point, index) => {
      const distance = haversineKm(current.position, point.position);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });
    ordered.push(remaining.splice(bestIndex, 1)[0]);
  }
  return ordered;
};

function AdminMap() {
  const [searchParams] = useSearchParams();
  const [devices, setDevices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showList, setShowList] = useState(true);
  const [pinnedPopupKey, setPinnedPopupKey] = useState(null);
  const [flyToPosition, setFlyToPosition] = useState(null);
  const [deviceFilter, setDeviceFilter] = useState('both');
  const [selectedIds, setSelectedIds] = useState([]);
  const [routeOrder, setRouteOrder] = useState([]);
  const [routeLine, setRouteLine] = useState([]);
  const [routeMessage, setRouteMessage] = useState('');
  const [routing, setRouting] = useState(false);
  const [mapStyle, setMapStyle] = useState('street');
  const [myLocation, setMyLocation] = useState(null);
  const [includeMyLocationInRoute, setIncludeMyLocationInRoute] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationMessage, setLocationMessage] = useState('');
  const focusAppliedRef = useRef('');

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/locations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setDevices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    const deviceId = searchParams.get('device');
    const kindParam = searchParams.get('kind') || 'inspection';
    const latParam = parseFloat(searchParams.get('lat'));
    const lonParam = parseFloat(searchParams.get('lon'));
    if (!deviceId && !(Number.isFinite(latParam) && Number.isFinite(lonParam))) return;

    const focusKey = `${deviceId || ''}|${kindParam}|${latParam}|${lonParam}`;
    if (focusAppliedRef.current === focusKey) return;

    if (kindParam === 'inspection' || kindParam === 'serviced') {
      setDeviceFilter(kindParam);
    }

    if (deviceId) {
      setSearchTerm(deviceId);
    }

    const match = (devices || []).find((device) => {
      if (deviceId && String(device.device_id) !== String(deviceId)) return false;
      if (kindParam === 'inspection' || kindParam === 'serviced') {
        return getKind(device) === kindParam;
      }
      return true;
    });

    if (match) {
      const lat = parseFloat(match.last_known_lat);
      const lon = parseFloat(match.last_known_lon);
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
        setPinnedPopupKey(deviceKey(match));
        setFlyToPosition([lat, lon]);
        focusAppliedRef.current = focusKey;
        return;
      }
    }

    if (Number.isFinite(latParam) && Number.isFinite(lonParam)) {
      setFlyToPosition([latParam, lonParam]);
      focusAppliedRef.current = focusKey;
    }
  }, [devices, searchParams]);

  const filteredDevices = useMemo(() => {
    const search = searchTerm.toLowerCase();
    return (devices || []).filter((device) => {
      const kind = getKind(device);
      if (deviceFilter === 'serviced' && kind !== 'serviced') return false;
      if (deviceFilter === 'inspection' && kind !== 'inspection') return false;

      const lat = parseFloat(device.last_known_lat);
      const lon = parseFloat(device.last_known_lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon) || (lat === 0 && lon === 0)) return false;

      if (!search) return true;
      return (
        String(device.device_id || '').toLowerCase().includes(search) ||
        (device.description && device.description.toLowerCase().includes(search)) ||
        (device.street_address && device.street_address.toLowerCase().includes(search)) ||
        (device.suburb && device.suburb.toLowerCase().includes(search)) ||
        (device.state && device.state.toLowerCase().includes(search)) ||
        (device.country && device.country.toLowerCase().includes(search)) ||
        (device.postcode && device.postcode.toLowerCase().includes(search)) ||
        (device.direction && device.direction.toLowerCase().includes(search)) ||
        kind.includes(search) ||
        kindLabel(kind).toLowerCase().includes(search)
      );
    });
  }, [devices, searchTerm, deviceFilter]);

  const onDeviceClick = (device) => {
    const key = deviceKey(device);
    setPinnedPopupKey(key);
    setFlyToPosition([parseFloat(device.last_known_lat), parseFloat(device.last_known_lon)]);
  };

  const toggleSelected = (key) => {
    setSelectedIds((prev) => (
      prev.includes(key)
        ? prev.filter((id) => id !== key)
        : [...prev, key]
    ));
  };

  const clearRoute = () => {
    setRouteOrder([]);
    setRouteLine([]);
    setRouteMessage('');
  };

  const locateMe = () => {
    if (!navigator.geolocation) {
      setLocationMessage('Geolocation is not supported on this device.');
      return;
    }
    setLocating(true);
    setLocationMessage('');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const next = [position.coords.latitude, position.coords.longitude];
        setMyLocation(next);
        setFlyToPosition(next);
        setLocating(false);
        setLocationMessage('Location found');
      },
      (error) => {
        console.warn('Geolocation failed', error);
        setLocating(false);
        setLocationMessage('Could not get your location. Check browser permissions.');
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  };

  const calculateRoute = async () => {
    const selectedDevices = (devices || []).filter((device) => {
      if (!selectedIds.includes(deviceKey(device))) return false;
      const lat = parseFloat(device.last_known_lat);
      const lon = parseFloat(device.last_known_lon);
      return Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0);
    });

    const useMyLocation = includeMyLocationInRoute && Array.isArray(myLocation);
    if (useMyLocation && selectedDevices.length < 1) {
      setRouteMessage('Select at least 1 device when using your location.');
      return;
    }
    if (!useMyLocation && selectedDevices.length < 2) {
      setRouteMessage('Select at least 2 devices to calculate a route.');
      return;
    }
    if (includeMyLocationInRoute && !myLocation) {
      setRouteMessage('Turn on My location first, then include it in the route.');
      return;
    }

    setRouting(true);
    setRouteMessage('');

    const devicePoints = selectedDevices.map((device) => ({
      id: device.device_id,
      kind: getKind(device),
      description: device.description,
      position: [parseFloat(device.last_known_lat), parseFloat(device.last_known_lon)]
    }));

    const startPoint = useMyLocation
      ? { id: 'My location', kind: 'me', description: 'Current location', position: myLocation }
      : null;

    let ordered = nearestNeighborOrder(devicePoints, startPoint);
    let line = ordered.map((point) => point.position);
    let source = 'straight-line estimate';

    try {
      const coordString = ordered
        .map((point) => `${point.position[1]},${point.position[0]}`)
        .join(';');
      const response = await fetch(
        `https://router.project-osrm.org/trip/v1/driving/${coordString}?source=first&roundtrip=false&geometries=geojson&overview=full`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.code === 'Ok' && data.waypoints && data.trips?.[0]?.geometry?.coordinates) {
          if (!useMyLocation) {
            const byTripIndex = Array(ordered.length);
            data.waypoints.forEach((waypoint, inputIndex) => {
              byTripIndex[waypoint.waypoint_index] = ordered[inputIndex];
            });
            ordered = byTripIndex.filter(Boolean);
          }
          line = data.trips[0].geometry.coordinates.map(([lon, lat]) => [lat, lon]);
          const distanceKm = (data.trips[0].distance || 0) / 1000;
          const durationMin = Math.round((data.trips[0].duration || 0) / 60);
          source = `road route · ${distanceKm.toFixed(1)} km · ~${durationMin} min`;
        }
      }
    } catch (error) {
      console.warn('OSRM route failed, using nearest-neighbor order', error);
    }

    setRouteOrder(ordered);
    setRouteLine(line);
    setRouteMessage(`Route ready (${source})`);
    setRouting(false);
  };

  const openGoogleMapsRoute = () => {
    if (routeOrder.length < 2) return;
    const origin = routeOrder[0].position;
    const destination = routeOrder[routeOrder.length - 1].position;
    const waypoints = routeOrder
      .slice(1, -1)
      .map((point) => `${point.position[0]},${point.position[1]}`)
      .join('|');
    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin[0]},${origin[1]}&destination=${destination[0]},${destination[1]}${waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : ''}&travelmode=driving`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const markerIcon = (device) => {
    if (selectedIds.includes(deviceKey(device)) && routeOrder.length) return selectedRouteIcon;
    if (getKind(device) === 'inspection') {
      return hasInspectionReport(device) ? inspectionDoneIcon : inspectionPendingIcon;
    }
    return servicedIcon;
  };

  return (
    <div style={{ height: '100vh', width: '100vw', position: 'relative' }}>
      {showList && (
        <div style={{
          position: 'absolute',
          top: 74,
          left: 10,
          width: 'min(340px, calc(100vw - 20px))',
          maxHeight: '80vh',
          backgroundColor: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          borderRadius: 8,
          overflowY: 'auto',
          zIndex: 1000,
          padding: 10,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#222' }}>Devices</div>
            <button
              type="button"
              onClick={() => setShowList(false)}
              style={{
                width: 'auto',
                padding: '5px 10px',
                borderRadius: 4,
                border: '1px solid #ccc',
                background: '#f5f5f5',
                color: '#333',
                cursor: 'pointer',
                fontSize: 12
              }}
              aria-label="Collapse device list"
            >
              Collapse
            </button>
          </div>

          <div style={{ display: 'flex', marginBottom: 10, gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                minWidth: 0,
                width: 'auto',
                padding: '6px 8px',
                borderRadius: 4,
                border: '1px solid #ccc',
                backgroundColor: '#f9f9f9',
                color: '#333',
                boxShadow: 'none'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {[
              ['both', 'All'],
              ['serviced', 'Serviced'],
              ['inspection', 'Inspection']
            ].map(([value, label]) => (
              <button
                key={value}
                onClick={() => setDeviceFilter(value)}
                style={{
                  flex: 1,
                  width: 'auto',
                  padding: '6px 4px',
                  borderRadius: 4,
                  border: '1px solid #ccc',
                  cursor: 'pointer',
                  backgroundColor: deviceFilter === value ? '#2196F3' : '#f3f3f3',
                  color: deviceFilter === value ? '#fff' : '#333',
                  fontSize: 12
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: 12, color: '#444', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#2196F3', display: 'inline-block' }} /> Serviced
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#FBC02D', display: 'inline-block' }} /> Pending
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#43A047', display: 'inline-block' }} /> Reported
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#1E88E5', display: 'inline-block', boxShadow: '0 0 0 3px rgba(30,136,229,0.25)' }} /> Me
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            <button
              type="button"
              onClick={locateMe}
              disabled={locating}
              style={{
                width: '100%',
                padding: '8px 6px',
                borderRadius: 4,
                border: '1px solid #90caf9',
                background: myLocation ? '#e3f2fd' : '#fff',
                color: '#1565c0',
                cursor: locating ? 'wait' : 'pointer',
                fontSize: 12,
                fontWeight: 600
              }}
            >
              {locating ? 'Locating...' : (myLocation ? 'Update my location' : 'Show my location')}
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#333', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeMyLocationInRoute}
                onChange={(e) => setIncludeMyLocationInRoute(e.target.checked)}
                disabled={!myLocation}
                style={{ width: 16, height: 16, minWidth: 16, maxWidth: 16, margin: 0, flexShrink: 0 }}
              />
              Include my location as route start
            </label>
            {locationMessage && (
              <div style={{ fontSize: 11, color: locationMessage.includes('Could not') || locationMessage.includes('not supported') ? '#c62828' : '#2e7d32' }}>
                {locationMessage}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <button
              onClick={calculateRoute}
              disabled={
                routing
                || (includeMyLocationInRoute
                  ? (!myLocation || selectedIds.length < 1)
                  : selectedIds.length < 2)
              }
              style={{
                flex: 1,
                width: 'auto',
                padding: '8px 6px',
                borderRadius: 4,
                border: 'none',
                backgroundColor: (
                  routing
                  || (includeMyLocationInRoute
                    ? (!myLocation || selectedIds.length < 1)
                    : selectedIds.length < 2)
                ) ? '#aaa' : '#4CAF50',
                color: '#fff',
                cursor: (
                  routing
                  || (includeMyLocationInRoute
                    ? (!myLocation || selectedIds.length < 1)
                    : selectedIds.length < 2)
                ) ? 'not-allowed' : 'pointer',
                fontSize: 12
              }}
            >
              {routing ? 'Calculating...' : `Route (${selectedIds.length}${includeMyLocationInRoute && myLocation ? '+me' : ''})`}
            </button>
            <button
              onClick={() => { setSelectedIds([]); clearRoute(); }}
              style={{
                width: 'auto',
                padding: '8px 10px',
                borderRadius: 4,
                border: '1px solid #ccc',
                background: '#fff',
                color: '#333',
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              Clear
            </button>
          </div>

          {routeMessage && (
            <div style={{ fontSize: 12, color: '#333', marginBottom: 8 }}>{routeMessage}</div>
          )}

          {routeOrder.length > 0 && (
            <div style={{ marginBottom: 10, padding: 8, background: '#f5f5f5', borderRadius: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Optimized order</div>
              <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#333' }}>
                {routeOrder.map((stop, index) => (
                  <li key={`${stop.kind}-${stop.id}`} style={{ marginBottom: 4 }}>
                    {index + 1}. {stop.id} ({kindLabel(stop.kind)})
                  </li>
                ))}
              </ol>
              <button
                onClick={openGoogleMapsRoute}
                style={{
                  marginTop: 8,
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: 4,
                  border: 'none',
                  backgroundColor: '#1976D2',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: 12
                }}
              >
                Open in Google Maps
              </button>
            </div>
          )}

          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {filteredDevices.map((device) => {
              const kind = getKind(device);
              const key = deviceKey(device);
              const isSelected = selectedIds.includes(key);
              return (
                <li
                  key={key}
                  style={{
                    padding: '8px 0',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start'
                  }}
                >
                  <button
                    type="button"
                    aria-label={isSelected ? 'Deselect for route' : 'Select for route'}
                    aria-pressed={isSelected}
                    onClick={() => toggleSelected(key)}
                    style={{
                      flex: '0 0 18px',
                      width: 18,
                      height: 18,
                      minWidth: 18,
                      maxWidth: 18,
                      marginTop: 2,
                      padding: 0,
                      borderRadius: 3,
                      border: isSelected ? '1px solid #2e7d32' : '1px solid #999',
                      background: isSelected ? '#4CAF50' : '#fff',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      lineHeight: 1,
                      boxShadow: 'none'
                    }}
                  >
                    {isSelected ? '✓' : ''}
                  </button>
                  <div style={{ cursor: 'pointer', flex: 1, minWidth: 0 }} onClick={() => onDeviceClick(device)}>
                    <div style={{ fontWeight: 600, color: '#222' }}>
                      {device.device_id}
                      <span style={{
                        marginLeft: 6,
                        fontSize: 11,
                        fontWeight: 500,
                        color: kind === 'inspection'
                          ? (hasInspectionReport(device) ? '#2E7D32' : '#F9A825')
                          : '#1976D2'
                      }}>
                        {kind === 'inspection' ? inspectionStatusLabel(device) : kindLabel(kind)}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#555' }}>{device.description || 'No description'}</div>
                  </div>
                </li>
              );
            })}
            {filteredDevices.length === 0 && <li>No devices found</li>}
          </ul>
        </div>
      )}

      {!showList && (
        <button
          onClick={() => setShowList(true)}
          style={{
            position: 'absolute',
            top: 74,
            left: 10,
            zIndex: 1000,
            width: 'auto',
            padding: '6px 12px',
            borderRadius: 4,
            border: 'none',
            backgroundColor: '#3498db',
            color: 'white',
            cursor: 'pointer',
          }}
          aria-label="Open device list"
        >
          Devices ▸
        </button>
      )}

      <div style={{
        position: 'absolute',
        bottom: 24,
        right: 70,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'stretch'
      }}>
        <button
          type="button"
          onClick={locateMe}
          disabled={locating}
          style={{
            width: 'auto',
            padding: '8px 12px',
            borderRadius: 4,
            border: 'none',
            cursor: locating ? 'wait' : 'pointer',
            backgroundColor: myLocation ? '#1565c0' : '#fff',
            color: myLocation ? '#fff' : '#1565c0',
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            fontSize: 12,
            fontWeight: 600
          }}
          aria-label="Show my location"
        >
          {locating ? 'Locating…' : 'My location'}
        </button>
        <div style={{
          display: 'flex',
          gap: 0,
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
        }}>
          {[
            ['street', 'Street'],
            ['satellite', 'Sat'],
            ['hybrid', 'Hybrid']
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setMapStyle(value)}
              style={{
                width: 'auto',
                padding: '7px 10px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: mapStyle === value ? '#1e88e5' : '#fff',
                color: mapStyle === value ? '#fff' : '#333',
                fontSize: 12,
                fontWeight: mapStyle === value ? 600 : 400
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <MapContainer
        center={[-27.084498, 152.979920]}
        zoom={10}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
      >
        <TileLayer
          key={`${mapStyle}-base`}
          attribution={MAP_LAYERS[mapStyle].attribution}
          url={MAP_LAYERS[mapStyle].url}
        />
        {(MAP_LAYERS[mapStyle].overlays || []).map((overlay) => (
          <TileLayer
            key={`${mapStyle}-${overlay.url}`}
            attribution={overlay.attribution}
            url={overlay.url}
            opacity={0.95}
          />
        ))}
        {flyToPosition && <FlyToMarker position={flyToPosition} />}
        {routeLine.length > 1 && <FitRoute positions={routeLine} />}
        {routeLine.length > 1 && (
          <Polyline positions={routeLine} pathOptions={{ color: '#FB8C00', weight: 5, opacity: 0.85 }} />
        )}
        {myLocation && (
          <Marker position={myLocation} icon={myLocationIcon}>
            <Popup autoClose={false} closeOnClick={false}>
              <strong>My location</strong>
            </Popup>
          </Marker>
        )}
        {filteredDevices.map((device) => {
          const key = deviceKey(device);
          return (
            <DeviceMarker
              key={key}
              device={device}
              icon={markerIcon(device)}
              isPinned={pinnedPopupKey === key}
              onPin={(nextKey) => {
                setPinnedPopupKey(nextKey);
              }}
              onUnpin={(closedKey) => {
                setPinnedPopupKey((prev) => (prev === closedKey ? null : prev));
              }}
              selectedIds={selectedIds}
              toggleSelected={toggleSelected}
            />
          );
        })}
        <ZoomControl position="bottomright" />
      </MapContainer>
    </div>
  );
}

export default AdminMap;
